<template>
  <router-view v-slot="{ Component, route }">
    <ReloadPWA />
    <PWAInstallBanner />
    <ReloadIntercom />
    <LoadHotjar />
    <transition
      :name="route.meta.transition || ''"
      mode="out-in"
    >
      <div :key="route.name">
        <component
          :is="Component"
          :key="accountStore.reload"
        />
      </div>
    </transition>
  </router-view>
</template>

<script lang="ts">
import { defineComponent } from "vue"

import ReloadPWA from "@/components/ReloadPWA.vue"
import PWAInstallBanner from "@/components/PWAInstallBanner.vue"
import ReloadIntercom from "./components/ReloadIntercom.vue"
import LoadHotjar from "@/components/LoadHotjar.vue"

import { useAccountStore } from "@/stores/account"
import { mapStores } from "pinia"
import { useAuthenticationStore } from "@/stores/authentication"
import { useEstablishmentStore } from "@/stores/establishment"
import { ensureAugusteAuthenticated } from "@/services/AugusteSessionService"
import { useTrackingStore } from "@/stores/tracking"
import { useRoute } from "vue-router"

export default defineComponent({
  name: "App",

  components: { LoadHotjar, ReloadPWA, PWAInstallBanner, ReloadIntercom },

  computed: {
    ...mapStores(useAccountStore, useAuthenticationStore, useEstablishmentStore, useTrackingStore),
  },

  created() {
    this.initApp()
  },

  methods: {
    async initApp() {
      if (this.authenticationStore.user && this.authenticationStore.user!.email != undefined) {
        await this.$router.isReady()

        const route = this.$router.currentRoute.value
        const checkoutEstablishmentId = route.query.establishmentId?.toString()
        const assistantEstablishmentId = route.query.establishment?.toString()
        const assistantConversationId = route.query.conversation?.toString()

        if (assistantEstablishmentId) {
          sessionStorage.setItem("requestedEstablishmentId", assistantEstablishmentId)
        }
        if (assistantConversationId) {
          sessionStorage.setItem("assistantReturnConversationId", assistantConversationId)
        }
        if (route.name === "assistant" || assistantConversationId || assistantEstablishmentId) {
          sessionStorage.setItem("pendingAssistantDeepLink", "1")
        }

        this.accountStore.getEstablishments(async () => {
          this.trackingStore.trackAppSession()
          this.accountStore.establishmentLoaded = true

          const requestedEstablishmentId =
            checkoutEstablishmentId ||
            assistantEstablishmentId ||
            sessionStorage.getItem("requestedEstablishmentId")

          if (requestedEstablishmentId) {
            await this.establishmentStore.initEstablishment(requestedEstablishmentId)
          } else {
            const currentEstablishment = this.establishmentStore.currentEstablishment
            if (currentEstablishment?.id) {
              // needed to refresh product plan if updated on nova
              await this.establishmentStore.getCurrentEstablishment(currentEstablishment.id)
            }
          }

          const pendingDeepLink = sessionStorage.getItem("pendingAssistantDeepLink") === "1"
          if (pendingDeepLink && this.accountStore.hasPremium) {
            sessionStorage.removeItem("pendingAssistantDeepLink")
            const est = sessionStorage.getItem("requestedEstablishmentId")
            const conv = sessionStorage.getItem("assistantReturnConversationId")
            const query: Record<string, string> = {}
            if (est) {
              query.establishment = est
            }
            if (conv) {
              query.conversation = conv
            }
            if (this.$route.name !== "assistant" || this.$route.query.conversation !== conv) {
              await this.$router.replace({ name: "assistant", query })
            }
          }

          ensureAugusteAuthenticated().catch(() => {
            // Le composant iframe affichera l'erreur et permettra de relancer l'authentification.
          })

          const language = this.authenticationStore.user?.language

          if (language) {
            this.$i18n.locale = this.authenticationStore.user.language
            this.$traduction.changeLang(language)
          }
        }, false)
      }
    },
  },
})
</script>
